// ./src/components/features/Pois/PoiEditDrawer/PoiEditDrawer.tsx
//
// Create/edit drawer for an event Point of Interest. Self-loading in edit mode via
// recordId; context-or-props resolution; no success toast (the page highlight is
// the confirmation). Fields: name, category, description (markdown), address, link,
// website, phone, email, price + currency + unit, status. `eventId` is set on
// CREATE only. Mirrors HighlightEditDrawer.

import React, { useState, useEffect, useCallback, useMemo, useRef, Suspense } from "react";
import { Stack, TextInput, NumberInput, Input, Skeleton } from "@mantine/core";

import { useTranslation } from "react-i18next";
import { OpResult } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";

import { PointOfInterest, PointOfInterestInput } from "@/utils/api";
import { getFormErrors, formatErrors, formatAddressFull } from "@/utils";
import { useModalShortcuts, SHORTCUT_HINTS } from "@/hooks";
import { PlaceData, AddressData } from "@/hooks/useGoogleMapsAutocomplete";
import { useOptionalPointsOfInterestContext } from "@/contexts/PointsOfInterestContext";
import {
  AppDrawer,
  StatusInput,
  CategorySelect,
  useCurrencyAffixes,
  PlaceAutocompleteInput,
  AddressAutocompleteInput,
} from "@/components/generic";

// Google place types treated as a real, lookup-able point of interest
// (hotels, restaurants, museums, attractions…) — same broad set as venues.
const POI_PLACE_TYPES = ["establishment"];

// The single line the POI stores is the ADDRESS only — the place's name has its
// own field right above it. Deliberately not `displayName`: Google sets that to
// the business name ("Tim Hortons") for an establishment, which would put the
// name in the address field and lose the street, city and region entirely.

// MarkdownField pulls in a heavy CodeMirror bundle — lazy-load it by path so it
// doesn't leak into the shared bundle (it's not re-exported from the generic barrel).
const MarkdownField = React.lazy(() =>
  import("@/components/generic/MarkdownField/MarkdownField").then((m) => ({ default: m.MarkdownField }))
);

export interface PoiEditDrawerProps {
  opened: boolean;
  onClose: () => void;
  recordId?: string | null;
  /** The event this POI belongs to — set on the input in CREATE mode only. */
  eventId?: string | null;
  onCreated?: (poi: PointOfInterest) => void;
  onUpdated?: (poi: PointOfInterest) => void;
}

interface FormData {
  categoryId: string | null;
  name: string;
  description: string;
  streetAddress: string;
  latitude: number | null;
  longitude: number | null;
  link: string;
  website: string;
  phone: string;
  email: string;
  price: number | "";
  priceUnit: string;
  status: number;
}

const KNOWN_FORM_FIELDS = [
  "categoryId",
  "name",
  "description",
  "streetAddress",
  "latitude",
  "longitude",
  "link",
  "website",
  "phone",
  "email",
  "price",
  "priceUnit",
  "status",
];

const getInitialFormData = (): FormData => ({
  categoryId: null,
  name: "",
  description: "",
  streetAddress: "",
  latitude: null,
  longitude: null,
  link: "",
  website: "",
  phone: "",
  email: "",
  price: "",
  priceUnit: "",
  status: STATUSES.ACTIVE,
});

const fromRecord = (record: PointOfInterest): FormData => ({
  categoryId: record.categoryId || null,
  name: record.name || "",
  description: record.description || "",
  streetAddress: record.streetAddress || "",
  latitude: record.latitude ?? null,
  longitude: record.longitude ?? null,
  link: record.link || "",
  website: record.website || "",
  phone: record.phone || "",
  email: record.email || "",
  price: record.price ?? "",
  priceUnit: record.priceUnit || "",
  status: record.status ?? STATUSES.ACTIVE,
});

const serialize = (data: FormData): string => JSON.stringify(data);

const PoiEditDrawerSkeleton: React.FC = () => (
  <Stack gap="md">
    <Skeleton height={60} radius="sm" />
    <Skeleton height={60} radius="sm" />
    <Skeleton height={120} radius="sm" />
    <Skeleton height={60} radius="sm" />
  </Stack>
);

export const PoiEditDrawer: React.FC<PoiEditDrawerProps> = ({
  opened,
  onClose,
  recordId,
  eventId,
  onCreated,
  onUpdated,
}) => {
  const { t } = useTranslation(["events", "common"]);
  // #A4.7b — the price is in the EVENT's currency, shown on the input (sign before, code after); not editable here.
  const currencyAffixes = useCurrencyAffixes();

  const ctx = useOptionalPointsOfInterestContext();
  const loadFn = ctx?.get;
  const createFn = ctx?.create;
  const updateFn = ctx?.update;
  const creating = ctx?.isCreating ?? false;
  const updating = ctx?.isUpdating ?? false;
  const operationError = ctx?.lastError ?? null;
  const clearOperationError = ctx?.clearError;

  const isEditMode = Boolean(recordId);
  const isSubmitting = creating || updating;

  const loadFnRef = useRef(loadFn);
  loadFnRef.current = loadFn;
  const clearErrRef = useRef(clearOperationError);
  clearErrRef.current = clearOperationError;

  // ── Self-loading ──
  const [record, setRecord] = useState<PointOfInterest | null>(null);
  const [isLoadingRecord, setIsLoadingRecord] = useState(false);
  const [loadError, setLoadError] = useState<OpResult | null>(null);

  const showLoadingSkeleton = isLoadingRecord && !record;
  // An edit drawer WITHOUT its record (the read failed) shows the error only — its form still held the last record's
  // values, and Save wrote them over this one (#131 review). Save and ⌘S are off until the record is read.
  const withoutRecord = isEditMode && !record;

  useEffect(() => {
    let isCancelled = false;

    if (opened && isEditMode && recordId && loadFnRef.current) {
      setIsLoadingRecord(true);
      setRecord(null);
      setLoadError(null);

      loadFnRef.current(recordId).then((result) => {
        if (isCancelled) {
          return;
        }

        if (result.didSucceed()) {
          setRecord(result.getDataFirst() as PointOfInterest);
          setLoadError(null);
        } else {
          setRecord(null);
          setLoadError(result);
        }

        setIsLoadingRecord(false);
      });
    } else if (!opened) {
      setRecord(null);
      setIsLoadingRecord(false);
      setLoadError(null);
    }

    return () => {
      isCancelled = true;
    };
  }, [opened, isEditMode, recordId]);

  // ── Form state ──
  const [formData, setFormData] = useState<FormData>(() => getInitialFormData());
  const initialSnapshotRef = useRef<string>("");
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!opened || showLoadingSkeleton || (isEditMode && !record)) {
      // Not dirty while closed, loading, or without its record (a failed read) — a stale flag from the last visit
      // asked "Discard changes?" over nothing (#131).
      setIsDirty(false);

      return;
    }

    setIsDirty(serialize(formData) !== initialSnapshotRef.current);
  }, [formData, opened, showLoadingSkeleton, isEditMode, record]);

  const effectiveError = loadError || operationError;

  const { fields: fieldErrors, genericErrors } = useMemo(
    () => getFormErrors(effectiveError, KNOWN_FORM_FIELDS),
    [effectiveError]
  );

  const formattedGenericError = useMemo(() => formatErrors(genericErrors), [genericErrors]);

  useEffect(() => {
    if (!opened) {
      return;
    }

    if (isEditMode && !record) {
      return;
    }

    const newFormData = record ? fromRecord(record) : getInitialFormData();
    setFormData(newFormData);
    initialSnapshotRef.current = serialize(newFormData);
    setIsDirty(false);
    clearErrRef.current?.();
  }, [opened, record, isEditMode]);

  const setField = useCallback((field: keyof FormData, value: FormData[keyof FormData]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  // Place search on the NAME field: picking a place fills in the address,
  // website, phone, coordinates (and a maps link if empty). Typing a plain name
  // leaves everything else untouched (placeData is null).
  const handleNameChange = useCallback((name: string, placeData: PlaceData | null) => {
    setFormData((prev) => {
      const next: FormData = { ...prev, name };

      if (placeData) {
        if (placeData.website) {
          next.website = placeData.website;
        }
        if (placeData.phone) {
          next.phone = placeData.phone;
        }
        if (placeData.googleMapsUri && !prev.link.trim()) {
          next.link = placeData.googleMapsUri;
        }
        if (placeData.address) {
          const addr = formatAddressFull(placeData.address);
          if (addr) {
            next.streetAddress = addr;
          }
          if (typeof placeData.address.latitude === "number") {
            next.latitude = placeData.address.latitude;
          }
          if (typeof placeData.address.longitude === "number") {
            next.longitude = placeData.address.longitude;
          }
        }
      }

      return next;
    });
  }, []);

  // Address search on the ADDRESS field: picking / geocoding an address fills the
  // single-line address plus coordinates; typing manually just updates the text.
  const handleAddressChange = useCallback((address1: string, fullAddress: AddressData | null) => {
    setFormData((prev) => ({
      ...prev,
      streetAddress: fullAddress ? formatAddressFull(fullAddress) : address1,
      latitude:
        fullAddress && typeof fullAddress.latitude === "number"
          ? fullAddress.latitude
          : address1.trim()
            ? prev.latitude
            : null,
      longitude:
        fullAddress && typeof fullAddress.longitude === "number"
          ? fullAddress.longitude
          : address1.trim()
            ? prev.longitude
            : null,
    }));
  }, []);

  // ── Submit ──
  const handleSubmit = useCallback(async () => {
    clearErrRef.current?.();
    setLoadError(null);

    const submitData: PointOfInterestInput = {
      categoryId: formData.categoryId,
      name: formData.name.trim() || null,
      description: formData.description.trim() || null,
      streetAddress: formData.streetAddress.trim() || null,
      latitude: formData.latitude,
      longitude: formData.longitude,
      link: formData.link.trim() || null,
      website: formData.website.trim() || null,
      phone: formData.phone.trim() || null,
      email: formData.email.trim() || null,
      price: formData.price === "" ? null : Number(formData.price),
      priceUnit: formData.priceUnit.trim() || null,
      status: formData.status,
    };

    if (!isEditMode) {
      submitData.eventId = eventId ?? null;
    }

    let result: OpResult | null = null;
    let isCreate = false;

    if (isEditMode && recordId && updateFn) {
      result = await updateFn(recordId, submitData);
    } else if (!isEditMode && createFn) {
      result = await createFn(submitData);
      isCreate = true;
    }

    if (result?.didSucceed()) {
      setIsDirty(false);
      const saved = result.data ? (result.data as PointOfInterest[])[0] : null;

      if (saved) {
        if (isCreate) {
          onCreated?.(saved);
        } else {
          onUpdated?.(saved);
        }
      }

      onClose();
    }
  }, [formData, isEditMode, recordId, eventId, createFn, updateFn, onClose, onCreated, onUpdated]);

  useModalShortcuts({
    onConfirm: handleSubmit,
    canConfirm: !isSubmitting && !showLoadingSkeleton && !withoutRecord,
    enabled: opened,
  });

  const drawerTitle = isEditMode
    ? t("events:pois.form.editTitle", { defaultValue: "Edit point of interest" })
    : t("events:pois.form.addTitle", { defaultValue: "Add point of interest" });

  // ⚠ EVERY key listed in KNOWN_FORM_FIELDS needs somewhere to land. `getFormErrors` puts a KNOWN name in
  // `fields` and NEVER in `genericErrors`, so a listed field with no control SWALLOWS its message entirely —
  // strictly worse than not listing it, because the save just fails with nothing on screen.
  // The coordinates have no control of their own here (the address input writes them), so they surface on it.
  const addressError = fieldErrors.streetAddress || fieldErrors.latitude || fieldErrors.longitude;

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      title={drawerTitle}
      position="right"
      size="md"
      closable={!isSubmitting}
      loading={isSubmitting}
      errors={formattedGenericError}
      hasFieldErrors={Object.values(fieldErrors).some(Boolean)}
      actionType="save-cancel"
      onConfirm={handleSubmit}
      confirmShortcut={SHORTCUT_HINTS.save}
      confirmDisabled={showLoadingSkeleton || withoutRecord}
      isDirty={isDirty}
    >
      {withoutRecord && !showLoadingSkeleton ? null : showLoadingSkeleton ? (
        <PoiEditDrawerSkeleton />
      ) : (
        <Stack gap="md">
          <PlaceAutocompleteInput
            label={t("events:pois.form.fields.name.label", { defaultValue: "Name" })}
            placeholder={t("events:pois.form.fields.name.placeholder", {
              defaultValue: "Search a place, or type a name",
            })}
            value={formData.name}
            onChange={handleNameChange}
            placeTypes={POI_PLACE_TYPES}
            error={fieldErrors.name}
            required
            withinPortal
            size="md"
          />

          <CategorySelect
            group="poi"
            label={t("events:pois.form.fields.category.label", { defaultValue: "Category" })}
            description={t("events:pois.form.fields.category.description", {
              defaultValue: "Group similar places (hotels, restaurants, museums).",
            })}
            value={formData.categoryId}
            onChange={(id) => setField("categoryId", id)}
            error={fieldErrors.categoryId}
            clearable
            size="md"
          />

          <Input.Wrapper
            label={t("events:pois.form.fields.description.label", { defaultValue: "Description" })}
            error={fieldErrors.description}
          >
            <Suspense fallback={<Skeleton height={180} radius="sm" />}>
              <MarkdownField
                value={formData.description}
                onChange={(md) => setField("description", md)}
                placeholder={t("events:pois.form.fields.description.placeholder", {
                  defaultValue: "What guests should know about this place…",
                })}
                minHeight={180}
              />
            </Suspense>
          </Input.Wrapper>

          <AddressAutocompleteInput
            label={t("events:pois.form.fields.streetAddress.label", { defaultValue: "Address" })}
            placeholder={t("events:pois.form.fields.streetAddress.placeholder", {
              defaultValue: "Search an address",
            })}
            value={formData.streetAddress}
            onChange={handleAddressChange}
            error={addressError}
            showLocateButton={false}
            withinPortal
            size="md"
          />

          <TextInput
            label={t("events:pois.form.fields.link.label", { defaultValue: "Link" })}
            placeholder={t("events:pois.form.fields.link.placeholder", {
              defaultValue: "Map or booking URL",
            })}
            value={formData.link}
            onChange={(e) => setField("link", e.currentTarget.value)}
            error={fieldErrors.link}
            size="md"
          />

          <TextInput
            label={t("events:pois.form.fields.website.label", { defaultValue: "Website" })}
            placeholder={t("events:pois.form.fields.website.placeholder", {
              defaultValue: "https://…",
            })}
            value={formData.website}
            onChange={(e) => setField("website", e.currentTarget.value)}
            error={fieldErrors.website}
            size="md"
          />

          <TextInput
            label={t("events:pois.form.fields.phone.label", { defaultValue: "Phone" })}
            placeholder={t("events:pois.form.fields.phone.placeholder", {
              defaultValue: "+1 555 123 4567",
            })}
            value={formData.phone}
            onChange={(e) => setField("phone", e.currentTarget.value)}
            error={fieldErrors.phone}
            size="md"
          />

          <TextInput
            label={t("events:pois.form.fields.email.label", { defaultValue: "Email" })}
            placeholder={t("events:pois.form.fields.email.placeholder", {
              defaultValue: "hello@example.com",
            })}
            value={formData.email}
            onChange={(e) => setField("email", e.currentTarget.value)}
            error={fieldErrors.email}
            size="md"
          />

          <NumberInput
            label={t("events:pois.form.fields.price.label", { defaultValue: "Price" })}
            value={formData.price}
            onChange={(value) => setField("price", value === "" ? "" : (value as number))}
            error={fieldErrors.price}
            min={0}
            decimalScale={2}
            step={0.01}
            thousandSeparator=","
            size="md"
            {...currencyAffixes}
          />

          <TextInput
            label={t("events:pois.form.fields.priceUnit.label", { defaultValue: "Price unit" })}
            placeholder={t("events:pois.form.fields.priceUnit.placeholder", {
              defaultValue: "per night / per person",
            })}
            value={formData.priceUnit}
            onChange={(e) => setField("priceUnit", e.currentTarget.value)}
            error={fieldErrors.priceUnit}
            size="md"
          />

          <StatusInput
            value={formData.status}
            onChange={(status) => setField("status", status)}
            error={fieldErrors.status}
          />
        </Stack>
      )}
    </AppDrawer>
  );
};
